import type { IUserStore } from '../../stores/UserStore';
import type { IStore } from '../../stores/Store';
import { parseIntOrNull } from '../../utils/numbers';
import { logError } from '../../utils/logError';
import type { IJobStore } from '../../stores/JobStore';
import { DEFAULT_ID } from '../../constants/job';
import type {
  BUCKETS,
  LowerCaseBucketName,
} from '../../stores/JobApplicationStore/types';

import type { IJobApplication } from '../../stores/JobApplicationStore/JobApplication';
import type { IJobApplicationStore } from '../../stores/JobApplicationStore';
import { transformRoleRequirementsIntoCertifications } from './mappers';
import type { IFiltersStore } from '../../stores/JobApplicationStore/FiltersStore/FiltersStore';
import type { IJobApplicationSummary } from '../../stores/JobApplicationStore/JobApplicationSummary';
import { DP_SEARCH_VIEW } from '../../constants/dataPlatform';

interface CertificationRRAnswer {
  answer_id: string | null;
  answer_global_id: string | null;
  is_certified: boolean;
}
export interface Certification {
  question_id: string | null;
  question_global_id: string | null;
  is_certified: boolean; // deprecated in data platform
  answers: CertificationRRAnswer[];
}

export interface HirerData {
  advertiserId: number;
  isSmallDevice: boolean;
  seekAdvertiserId: number;
  userId: number | null;
}

export type DataPlatformSearchBucket = 'search';

export const buildHirerData = ({
  userStore,
  store,
  eventType,
}: {
  userStore: IUserStore;
  store: IStore;
  eventType: string;
}): HirerData | undefined => {
  const { advertiserId, seekAdvertiserId, isMobile } = store;
  const { user } = userStore;

  const parsedAdvertiserId: number | null = parseIntOrNull(advertiserId);
  const parsedSeekAdvertiserId: number | null =
    parseIntOrNull(seekAdvertiserId);
  if (!parsedAdvertiserId || !parsedSeekAdvertiserId) {
    logError(
      {
        errorCode: 'EVENT_CAPTURE_MISSING_ADVERTISER_DATA',
        description:
          'Missing advertiser data: [advertiserId, seekAdvertiserId]',
      },
      {
        eventType,
        advertiserId,
        seekAdvertiserId,
      },
    );
    return;
  }

  return {
    advertiserId: parsedAdvertiserId,
    seekAdvertiserId: parsedSeekAdvertiserId,
    isSmallDevice: isMobile,
    userId: user.userId || null,
  };
};

export interface JobData {
  hasRR: boolean;
  jobId: number;
  listingId: number;
  positionId: number;
}

export const buildJobData = ({
  jobStore,
  eventType,
}: {
  jobStore: IJobStore;
  eventType: string;
}): JobData | undefined => {
  const { job, questionnaire } = jobStore;
  const { jobInformation, positionId } = job;
  const { jobId, listingId } = jobInformation;

  if (!jobId) {
    logError(
      {
        errorCode: 'EVENT_CAPTURE_MISSING_JOB_ID',
        description: 'Missing job id',
      },
      {
        eventType,
        listingId,
        positionId,
      },
    );
    return;
  }

  if (!positionId || positionId === DEFAULT_ID) {
    logError(
      {
        errorCode: 'EVENT_CAPTURE_MISSING_POSITION_ID',
        description: 'Missing position id',
      },
      {
        eventType,
        jobId,
        listingId,
      },
    );
    return;
  }
  if (!listingId || listingId === DEFAULT_ID) {
    logError(
      {
        errorCode: 'EVENT_CAPTURE_MISSING_LISTING_ID',
        description: 'Missing listing id',
      },
      {
        eventType,
        positionId,
        jobId,
      },
    );
    return;
  }

  return {
    hasRR: questionnaire.hasQuestion,
    jobId,
    listingId,
    positionId,
  };
};
const getPageBucket = (
  jobApplicationStore: IJobApplicationStore,
): LowerCaseBucketName | DataPlatformSearchBucket => {
  const { hasSearchText, bucket } = jobApplicationStore;
  return hasSearchText
    ? DP_SEARCH_VIEW
    : (bucket.toLowerCase() as LowerCaseBucketName);
};

export interface SummaryCandidateData {
  pageBucket: LowerCaseBucketName | DataPlatformSearchBucket;
  prospectId: number;
  positionInList: number;
}

export const buildCandidateDataFromSummaryJobApplication = ({
  jobApplication,
  jobApplicationStore,
  eventType,
}: {
  jobApplication: IJobApplicationSummary;
  jobApplicationStore: IJobApplicationStore;
  eventType: string;
}): SummaryCandidateData | undefined => {
  const parsedProspectId: number | null = parseIntOrNull(jobApplication.id);

  if (!parsedProspectId) {
    logError(
      {
        errorCode: 'EVENT_CAPTURE_MISSING_SUMMARY_CANDIDATE_DATA',
        description: 'Missing candidate data: prospectId',
      },
      {
        eventType,
        prospectId: jobApplication.id,
      },
    );
    return;
  }

  return {
    positionInList: jobApplication.positionInList,
    pageBucket: getPageBucket(jobApplicationStore),
    prospectId: parsedProspectId,
  };
};

export interface CandidateData {
  candidateId: number;
  seekCandidateId: number | null;
  certifications: Certification[];
  pageBucket: LowerCaseBucketName | DataPlatformSearchBucket;
  pageNumber: number;
  prospectId: number;
  seekJobApplicationId: number | null;
}

export const buildBaseCandidateData = ({
  jobApplication,
  jobApplicationStore,
  eventType,
}: {
  jobApplication: IJobApplication;
  jobApplicationStore: IJobApplicationStore;
  eventType: string;
}): CandidateData | undefined => {
  const { currentPage } = jobApplicationStore;
  const parsedCandidateId: number | null = parseIntOrNull(
    jobApplication.candidateId,
  );
  const parsedSeekCandidateId: number | null = parseIntOrNull(
    jobApplication.seekCandidateId,
  );
  const parsedProspectId: number | null = parseIntOrNull(jobApplication.id);

  if (!parsedCandidateId || !parsedProspectId) {
    logError(
      {
        errorCode: 'EVENT_CAPTURE_MISSING_CANDIDATE_DATA',
        description: 'Missing candidate data: [seekCandidateId, prospectId]',
      },
      {
        eventType,
        prospectId: jobApplication.id,
        seekCandidateId: jobApplication.seekCandidateId,
      },
    );
    return;
  }

  return {
    candidateId: parsedCandidateId,
    seekCandidateId: parsedSeekCandidateId,
    certifications: transformRoleRequirementsIntoCertifications(
      jobApplication.roleRequirements,
    ),
    pageBucket: getPageBucket(jobApplicationStore),
    pageNumber: currentPage,
    prospectId: parsedProspectId,
    seekJobApplicationId: parseIntOrNull(jobApplication.jobApplicationId),
  };
};

type DataPlatformFilterAppliedToBucket =
  | {
      filter_type: 'roleRequirements';
      question_id: string | null;
      question_global_id: string;
      answer_ids: string[];
      answer_global_ids: string[];
    }
  | {
      filter_type: 'locationFilter';
      searchLocationId: string[];
    };

export interface FiltersEventCaptureData {
  total_apps_in_bucket: number;
  filtered_apps_in_bucket: number;
  filters_applied_to_bucket: DataPlatformFilterAppliedToBucket[];
}

export const buildFiltersData = ({
  jobApplicationStore,
  filtersStore,
  homeLocationIds,
}: {
  jobApplicationStore: IJobApplicationStore;
  filtersStore: IFiltersStore;
  homeLocationIds: string[];
}): FiltersEventCaptureData => {
  const total_apps_in_bucket = jobApplicationStore.hasSearchText
    ? jobApplicationStore.currentPreFilterTotalOfJobApplications
    : jobApplicationStore.bucketStats.getBucketCount(
        jobApplicationStore.bucket as BUCKETS,
      );

  const filtered_apps_in_bucket =
    jobApplicationStore.currentTotalOfJobApplications;

  const filters_applied_to_bucket: DataPlatformFilterAppliedToBucket[] =
    filtersStore.selectedQuestionAnswers.map(({ question, answers }) => ({
      question_id: question.id,
      question_global_id: question.globalIdOrId,
      answer_ids: answers
        .map((answer) => answer.id)
        .filter((id): id is string => id !== null),
      answer_global_ids: answers.map((answer) => answer.globalIdOrId),
      filter_type: 'roleRequirements',
    }));

  if (homeLocationIds.length > 0) {
    filters_applied_to_bucket.push({
      filter_type: 'locationFilter',
      searchLocationId: homeLocationIds,
    });
  }

  return {
    total_apps_in_bucket,
    filtered_apps_in_bucket,
    filters_applied_to_bucket,
  };
};
